import { memo, useEffect, useRef } from 'react'
import uPlot from 'uplot'
import 'uplot/dist/uPlot.min.css'
import { traceColor, type LapTrace } from '../analysis/laps'
import { sectionAt, type Section } from '../analysis/sections'

/** Live plots per sync key, so the map (or anything else) can drive the shared cursor. */
const registry = new Map<string, Set<uPlot>>()

/**
 * After a tap, browsers synthesise mouse events, ending in a mouseleave on whatever the previous
 * tap touched. That would hide the cursor the finger just placed, on every synced chart. Track
 * touches globally so any mouseleave shortly after a touch is ignored.
 */
let lastTouch = 0
/** Called whenever a second finger lands anywhere: a pending one-finger anchor is dropped. */
const multiTouchListeners = new Set<() => void>()
if (typeof document !== 'undefined') {
  const note = (ev: TouchEvent) => {
    lastTouch = Date.now()
    if (ev.touches.length >= 2) for (const f of multiTouchListeners) f()
  }
  document.addEventListener('touchstart', note, { passive: true, capture: true })
  document.addEventListener('touchend', note, { passive: true, capture: true })
}
export function recentlyTouched(): boolean {
  return Date.now() - lastTouch < 800
}
export function onMultiTouch(fn: () => void): () => void {
  multiTouchListeners.add(fn)
  return () => {
    multiTouchListeners.delete(fn)
  }
}
/**
 * One finger on a line anchors, two fingers zoom: the first finger waits this long for a
 * second one before it counts as an anchor (a drag or a lift settles it sooner).
 */
export const PINCH_WAIT_MS = 200

/** Move the crosshair on every chart in the group to a distance, or hide it with null. */
export function setSyncCursor(syncKey: string, distance: number | null): void {
  const plots = registry.get(syncKey)
  if (!plots) return
  for (const u of plots) {
    if (distance == null) u.setCursor({ left: -10, top: -10 })
    else u.setCursor({ left: u.valToPos(distance, 'x'), top: 10 })
  }
}

// ---- Pinned anchor, shared by every chart in a group and the track map ----
// Mouse: while nothing is pinned the anchor follows the pointer when it is on a line; a click on
// a line pins it there; while pinned, hovering changes nothing; a click on another spot of a line
// moves the pin, a click on empty space (or Esc) clears it. Touch: a tap on a line pins it.
const pins = new Map<string, number>()
const pinListeners = new Set<() => void>()

export function pinnedAt(syncKey: string): number | null {
  return pins.get(syncKey) ?? null
}
export function setPin(syncKey: string, distance: number | null): void {
  if (distance == null) pins.delete(syncKey)
  else pins.set(syncKey, distance)
  for (const f of pinListeners) f()
}
export function subscribePins(fn: () => void): () => void {
  pinListeners.add(fn)
  return () => {
    pinListeners.delete(fn)
  }
}
/** Unpin and hide the anchor everywhere in the group. */
export function clearAnchor(syncKey: string): void {
  setPin(syncKey, null)
  setSyncCursor(syncKey, null)
}
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    for (const key of registry.keys()) if (pins.has(key)) clearAnchor(key)
  })
}

/** How close (px) a pointer must be to a trace to count as "on the line". */
const MOUSE_NEAR_PX = 12
const TOUCH_NEAR_PX = 24

/** Is the point (relative to the plot area) within `px` of any trace at that distance? */
function nearTrace(u: uPlot, left: number, top: number, px: number): boolean {
  if (left < 0 || top < 0 || left > u.over.clientWidth || top > u.over.clientHeight) return false
  const idx = u.posToIdx(left)
  for (let s = 1; s < u.data.length; s++) {
    const v = u.data[s][idx] as number | null | undefined
    if (v == null || !Number.isFinite(v)) continue
    if (Math.abs(u.valToPos(v, 'y') - top) <= px) return true
  }
  return false
}

export interface ChannelChartProps {
  title: string
  unit: string
  traces: LapTrace[]
  /** Series values per trace, aligned to trace.distance. Must be referentially stable. */
  values: (trace: LapTrace) => Float64Array | undefined
  /** Shared cursor group key so every chart shows the same crosshair */
  syncKey: string
  height?: number
  /** Draw a zero line, used for the delta-time strip */
  zeroLine?: boolean
  /** Track sections: dashed boundaries, and the section under the anchor is shaded */
  sections?: Section[]
  onCursor?: (distance: number | null) => void
  /** Laps whose line is hidden (by key); shared across every diagram */
  hidden?: Set<string>
  /** A click on a lap's name in the legend: show or hide it everywhere */
  onToggleLap?: (key: string) => void
}

/**
 * A single-measure strip chart; one chart per channel, never a dual axis.
 * Memoised so cursor-driven re-renders of the parent do not rebuild the plot.
 */
function ChannelChart({
  title,
  unit,
  traces,
  values,
  syncKey,
  height = 300,
  zeroLine = false,
  sections,
  onCursor,
  hidden,
  onToggleLap,
}: ChannelChartProps) {
  const host = useRef<HTMLDivElement>(null)
  const plot = useRef<uPlot | null>(null)
  const cursorCb = useRef(onCursor)
  useEffect(() => {
    cursorCb.current = onCursor
  }, [onCursor])
  // Hiding a lap toggles its series in place.
  useEffect(() => {
    const u = plot.current
    if (!u) return
    traces.forEach((t, i) => u.setSeries(i + 1, { show: !hidden?.has(t.key) }))
  }, [hidden, traces])
  const toggleRef = useRef(onToggleLap)
  toggleRef.current = onToggleLap
  const hiddenRef = useRef(hidden)
  hiddenRef.current = hidden
  // A new height resizes the plot in place; only data and options rebuild it.
  const heightRef = useRef(height)
  useEffect(() => {
    heightRef.current = height
    const el = host.current
    if (el && plot.current) plot.current.setSize({ width: el.clientWidth, height })
  }, [height])

  useEffect(() => {
    const el = host.current
    if (!el || traces.length === 0) return

    // All traces share one distance axis: use the longest lap's grid and interpolate the rest.
    const base = traces.reduce((a, b) => (a.distance.length >= b.distance.length ? a : b))
    const x = Array.from(base.distance)
    const data: uPlot.AlignedData = [
      x,
      ...traces.map((t) => {
        const v = values(t)
        if (!v) return x.map(() => null)
        if (t === base) return Array.from(v)
        return x.map((d) => (d <= t.distance[t.distance.length - 1] ? interp(t.distance, v, d) : null))
      }),
    ]

    const decimals = unit === 's' ? 3 : unit === 'deg' || unit === 'km/h' || unit === 'mph' ? 1 : 2
    const fmt = (v: number | null) => (v == null ? '–' : `${v.toFixed(decimals)}${unit ? ' ' + unit : ''}`)
    const fmtX = (v: number | null) => (v == null ? '–' : `${Math.round(v)} m`)
    const series: uPlot.Series[] = [
      { label: 'Distance', value: (_u: uPlot, v: number | null) => fmtX(v) },
      ...traces.map((t, i) => ({
        label: t.label,
        stroke: traceColor(t, i),
        width: 2,
        spanGaps: false,
        value: (_u: uPlot, v: number | null) => fmt(v),
      })),
    ]

    // Hover guides: a dotted line from each series' point to the y axis, plus the value on the axis.
    const guides: { line: HTMLDivElement; label: HTMLDivElement }[] = []
    const hover = new HoverGuides(
      traces.map((t, i) => traceColor(t, i)),
      fmt,
      guides,
    )
    const secs = sections ?? []
    // Shades the section the anchor is in; a DOM overlay, so the canvas is not redrawn per move.
    let shade: HTMLDivElement | null = null
    const shadeSection = (u: uPlot, idx: number | null) => {
      if (!shade) return
      const i = idx == null ? -1 : sectionAt(secs, u.data[0][idx] as number)
      if (i < 0) {
        shade.style.display = 'none'
        return
      }
      const left = u.valToPos(secs[i].from, 'x')
      const right = u.valToPos(secs[i].to, 'x')
      shade.style.display = 'block'
      shade.style.left = `${left}px`
      shade.style.width = `${Math.max(1, right - left)}px`
    }

    const opts: uPlot.Options = {
      width: el.clientWidth,
      height: heightRef.current,
      title,
      cursor: {
        sync: { key: syncKey, setSeries: false },
        x: true,
        y: false,
        drag: { x: false, y: false },
        points: { size: 8, width: 2 },
        // Mouse rules (see pins above). The mouse events a browser synthesises after a tap would
        // fight the touch handling below, so they are ignored right after a touch.
        bind: {
          mousemove: (self, _t, handler) => (e: MouseEvent) => {
            if (recentlyTouched() || pins.has(syncKey)) return null
            const r = self.over.getBoundingClientRect()
            if (nearTrace(self, e.clientX - r.left, e.clientY - r.top, MOUSE_NEAR_PX)) return handler(e)
            // Off the lines with nothing pinned: no anchor.
            if (self.cursor.idx != null) setSyncCursor(syncKey, null)
            return null
          },
          mouseleave: (_self, _t, handler) => (e: MouseEvent) =>
            recentlyTouched() || pins.has(syncKey) ? null : handler(e),
          mousedown: (_self, _t, handler) => (e: MouseEvent) => (recentlyTouched() ? null : handler(e)),
          mouseup: (_self, _t, handler) => (e: MouseEvent) => (recentlyTouched() ? null : handler(e)),
        },
      },
      select: { show: false, left: 0, top: 0, width: 0, height: 0 },
      legend: { show: true, live: true },
      scales: { x: { time: false } },
      axes: [
        {
          stroke: '#c9ceda',
          grid: { stroke: '#2a2f3a', width: 1 },
          ticks: { stroke: '#2a2f3a' },
          font: '12px system-ui, sans-serif',
          values: (_u, vals) => vals.map((v) => `${Math.round(v)} m`),
        },
        {
          stroke: '#c9ceda',
          grid: { stroke: '#2a2f3a', width: 1 },
          ticks: { stroke: '#2a2f3a' },
          font: '12px system-ui, sans-serif',
          size: 64,
          label: unit || undefined,
          labelFont: '12px system-ui, sans-serif',
          values: (_u, vals) => vals.map((v) => formatTick(v)),
        },
      ],
      series,
      hooks: {
        init: [
          (u) => {
            hover.mount(u)
            reserveLegendWidths(u, data, fmtX, fmt)
            if (secs.length) {
              shade = document.createElement('div')
              shade.className = 'section-shade'
              u.over.appendChild(shade)
            }
          },
        ],
        setCursor: [
          (u) => {
            const idx = u.cursor.idx
            hover.update(u, idx ?? null)
            shadeSection(u, idx ?? null)
            cursorCb.current?.(idx == null ? null : (u.data[0][idx] as number))
          },
        ],
        draw: [
          ...(zeroLine
            ? [
                (u: uPlot) => {
                  const y = u.valToPos(0, 'y', true)
                  const ctx = u.ctx
                  ctx.save()
                  ctx.strokeStyle = '#8b93a7'
                  ctx.setLineDash([4, 4])
                  ctx.beginPath()
                  ctx.moveTo(u.bbox.left, y)
                  ctx.lineTo(u.bbox.left + u.bbox.width, y)
                  ctx.stroke()
                  ctx.restore()
                },
              ]
            : []),
          // Section boundaries: a dashed line at each, the section's name at its top-left.
          ...(secs.length
            ? [
                (u: uPlot) => {
                  const ctx = u.ctx
                  const { left, top, width, height } = u.bbox
                  const dpr = devicePixelRatio || 1
                  ctx.save()
                  ctx.beginPath()
                  ctx.rect(left, top, width, height)
                  ctx.clip()
                  ctx.strokeStyle = '#8b93a7'
                  ctx.lineWidth = dpr
                  ctx.setLineDash([5 * dpr, 4 * dpr])
                  ctx.font = `${11 * dpr}px system-ui, sans-serif`
                  ctx.fillStyle = '#8b93a7'
                  ctx.textBaseline = 'top'
                  // uPlot leaves the axis-label alignment on the context.
                  ctx.textAlign = 'left'
                  for (const sec of secs) {
                    const x = u.valToPos(sec.from, 'x', true)
                    if (sec.index > 0) {
                      ctx.beginPath()
                      ctx.moveTo(x, top)
                      ctx.lineTo(x, top + height)
                      ctx.stroke()
                    }
                    // The name only where the section is wide enough for it (narrow phone charts).
                    const room = u.valToPos(sec.to, 'x', true) - x - 8 * dpr
                    if (ctx.measureText(sec.name).width <= room) ctx.fillText(sec.name, x + 4 * dpr, top + 3 * dpr)
                  }
                  ctx.restore()
                },
              ]
            : []),
        ],
      },
    }

    const u = new uPlot(opts, data, el)
    // Legend clicks belong to the owner, which hides the lap on every diagram, not just here.
    const legendClick = (e: MouseEvent) => {
      const row = (e.target as Element).closest('.u-legend .u-series')
      if (!row) return
      e.stopPropagation()
      e.preventDefault()
      const idx = [...u.root.querySelectorAll('.u-legend .u-series')].indexOf(row) - 1
      if (idx >= 0 && traces[idx]) toggleRef.current?.(traces[idx].key)
    }
    u.root.addEventListener('click', legendClick, true)
    traces.forEach((t, i) => {
      if (hiddenRef.current?.has(t.key)) u.setSeries(i + 1, { show: false })
    })
    // Exposed for the headless UI checks.
    ;(el as unknown as { __uplot?: uPlot }).__uplot = u
    plot.current = u
    let group = registry.get(syncKey)
    if (!group) {
      group = new Set()
      registry.set(syncKey, group)
    }
    group.add(u)

    // Place the anchor at a plot position on every synced chart and pin it there.
    // setCursor only publishes to the synced charts when asked (third argument).
    const pinAt = (left: number, top: number) => {
      const setCursor = u.setCursor as (opts: { left: number; top: number }, fire?: boolean, pub?: boolean) => void
      setCursor({ left, top }, true, true)
      setPin(syncKey, u.data[0][u.posToIdx(left)] as number)
    }

    // A pin set before this chart was (re)built, e.g. when laps changed, is restored.
    const pinned = pins.get(syncKey)
    if (pinned != null) u.setCursor({ left: u.valToPos(pinned, 'x'), top: 10 })
    const onResize = () => shadeSection(u, u.cursor.idx ?? null)

    // Mouse click: on a line pins (or moves the pin) there; anywhere else in the chart clears it.
    // Legend clicks keep their own meaning (show / hide a lap).
    const onClick = (e: MouseEvent) => {
      if (recentlyTouched()) return
      if ((e.target as Element).closest('.u-legend')) return
      const r = u.over.getBoundingClientRect()
      const left = e.clientX - r.left
      const top = e.clientY - r.top
      if (nearTrace(u, left, top, MOUSE_NEAR_PX)) pinAt(left, top)
      else clearAnchor(syncKey)
    }
    u.root.addEventListener('click', onClick)

    // Touch: a touch on (near) a trace pins the anchor there and a drag that started on a trace
    // moves it; a touch anywhere else, e.g. the start of a scroll, leaves it alone.
    let dragging = false
    // Plot coordinates of a touch, through any CSS scale (the full-screen zoom).
    const plotPos = (t: Touch) => {
      const r = u.over.getBoundingClientRect()
      const k = r.width > 0 ? u.over.clientWidth / r.width : 1
      return { left: (t.clientX - r.left) * k, top: (t.clientY - r.top) * k }
    }
    // A finger on a line is a pending anchor for PINCH_WAIT_MS: a second finger in that time
    // makes it a zoom instead; a drag, a lift or the wait makes it the anchor.
    let pending: { left: number; top: number } | null = null
    let timer = 0
    const settle = () => {
      if (!pending) return
      pinAt(pending.left, pending.top)
      pending = null
      dragging = true
    }
    const cancel = () => {
      pending = null
      dragging = false
      clearTimeout(timer)
    }
    const onTouchStart = (ev: TouchEvent) => {
      if (ev.touches.length !== 1) {
        cancel()
        return
      }
      const { left, top } = plotPos(ev.touches[0])
      if (!nearTrace(u, left, top, TOUCH_NEAR_PX)) return
      pending = { left, top }
      clearTimeout(timer)
      timer = window.setTimeout(settle, PINCH_WAIT_MS)
    }
    const onTouchMove = (ev: TouchEvent) => {
      if (ev.touches.length !== 1) return
      if (pending) {
        clearTimeout(timer)
        settle()
      }
      if (!dragging) return
      const { left, top } = plotPos(ev.touches[0])
      pinAt(left, top)
    }
    const onTouchEnd = () => {
      clearTimeout(timer)
      settle()
      dragging = false
    }
    const offMulti = onMultiTouch(cancel)
    u.over.addEventListener('touchstart', onTouchStart, { passive: true })
    u.over.addEventListener('touchmove', onTouchMove, { passive: true })
    u.over.addEventListener('touchend', onTouchEnd, { passive: true })
    u.over.addEventListener('touchcancel', cancel, { passive: true })

    const ro = new ResizeObserver(() => {
      plot.current?.setSize({ width: el.clientWidth, height: heightRef.current })
      onResize()
    })
    ro.observe(el)
    return () => {
      ro.disconnect()
      clearTimeout(timer)
      offMulti()
      registry.get(syncKey)?.delete(u)
      u.over.removeEventListener('touchstart', onTouchStart)
      u.over.removeEventListener('touchend', onTouchEnd)
      u.over.removeEventListener('touchmove', onTouchMove)
      u.over.removeEventListener('touchcancel', cancel)
      u.root.removeEventListener('click', onClick)
      u.root.removeEventListener('click', legendClick, true)
      plot.current?.destroy()
      plot.current = null
    }
  }, [traces, values, title, unit, syncKey, zeroLine, sections])

  return <div ref={host} className="chart" />
}

export default memo(ChannelChart)

class HoverGuides {
  private colors: string[]
  private fmt: (v: number | null) => string
  private els: { line: HTMLDivElement; label: HTMLDivElement }[]

  constructor(
    colors: string[],
    fmt: (v: number | null) => string,
    els: { line: HTMLDivElement; label: HTMLDivElement }[],
  ) {
    this.colors = colors
    this.fmt = fmt
    this.els = els
  }

  mount(u: uPlot) {
    for (let i = 0; i < this.colors.length; i++) {
      const color = this.colors[i]
      const line = document.createElement('div')
      line.className = 'hover-line'
      line.style.borderTopColor = color
      u.over.appendChild(line)
      const label = document.createElement('div')
      label.className = 'hover-ylabel'
      label.style.background = color
      u.root.querySelector('.u-wrap')?.appendChild(label)
      this.els.push({ line, label })
    }
  }

  update(u: uPlot, idx: number | null) {
    const axisWidth = u.over.offsetLeft
    const top0 = u.over.offsetTop
    const cursorX = u.cursor.left ?? 0
    this.els.forEach(({ line, label }, i) => {
      const v = idx == null ? null : (u.data[i + 1][idx] as number | null)
      if (v == null || !Number.isFinite(v)) {
        line.style.display = 'none'
        label.style.display = 'none'
        return
      }
      const y = u.valToPos(v, 'y')
      line.style.display = 'block'
      line.style.top = `${y}px`
      line.style.width = `${Math.max(0, cursorX)}px`
      label.style.display = 'block'
      label.style.top = `${top0 + y - 9}px`
      label.style.width = `${axisWidth - 4}px`
      label.textContent = this.fmt(v)
    })
  }
}

/**
 * Give every legend value a fixed width, as wide as the longest value its series can show, so the
 * legend (and the chart below it) does not shift or rewrap when the anchor appears and disappears.
 */
function reserveLegendWidths(
  u: uPlot,
  data: uPlot.AlignedData,
  fmtX: (v: number | null) => string,
  fmt: (v: number | null) => string,
) {
  // The init hook runs before uPlot stores the data, so the caller passes it in.
  const cells = u.root.querySelectorAll<HTMLElement>('.u-legend .u-series .u-value')
  cells.forEach((cell, s) => {
    const col = data[s] as ArrayLike<number | null> | undefined
    if (!col) return
    let lo = Infinity
    let hi = -Infinity
    for (let i = 0; i < col.length; i++) {
      const v = col[i]
      if (v == null || !Number.isFinite(v)) continue
      if (v < lo) lo = v
      if (v > hi) hi = v
    }
    const f = s === 0 ? fmtX : fmt
    const longest = Number.isFinite(lo) ? Math.max(f(lo).length, f(hi).length, f(-Math.abs(hi)).length) : 1
    cell.style.display = 'inline-block'
    cell.style.minWidth = `${longest + 0.5}ch`
    cell.style.textAlign = 'left'
  })
}

function formatTick(v: number): string {
  if (Math.abs(v) >= 100) return v.toFixed(0)
  if (Math.abs(v) >= 10) return v.toFixed(1)
  return Number.isInteger(v) ? v.toFixed(0) : v.toFixed(2)
}

function interp(xs: Float64Array, ys: Float64Array, x: number): number {
  let lo = 0
  let hi = xs.length - 1
  if (x <= xs[0]) return ys[0]
  if (x >= xs[hi]) return ys[hi]
  while (hi - lo > 1) {
    const mid = (lo + hi) >>> 1
    if (xs[mid] <= x) lo = mid
    else hi = mid
  }
  const f = (x - xs[lo]) / (xs[hi] - xs[lo])
  return ys[lo] + f * (ys[hi] - ys[lo])
}
